// src/components/User/LineLoginButton.tsx
import React, { useCallback, useMemo, useState } from "react";
import { setAuth } from "../../lib/auth"; // ← 相対パス修正（../ ではなく ../../）

let liffObj: any = null;
async function ensureLiff() {
  if (liffObj) return liffObj;
  try {
    const mod = await import("@line/liff");
    liffObj = mod.default;
    return liffObj;
  } catch {
    return null;
  }
}

type Props = {
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  /** Login.tsx 互換 */
  onSuccess?: (payload: { token: string | null; user: any }) => void;
};

const sizes: Record<NonNullable<Props["size"]>, React.CSSProperties> = {
  sm: { padding: "8px 14px", fontSize: 14, borderRadius: 10 },
  md: { padding: "12px 18px", fontSize: 16, borderRadius: 12 },
  lg: { padding: "14px 22px", fontSize: 18, borderRadius: 14 },
  xl: { padding: "16px 26px", fontSize: 20, borderRadius: 16 },
};

export default function LineLoginButton({ size = "md", className, onSuccess }: Props) {
  const [error, setError] = useState<string | null>(null);

  const LIFF_ID = (import.meta as any)?.env?.VITE_LIFF_ID || (process.env as any)?.REACT_APP_LIFF_ID;
  const CHANNEL_ID =
    (import.meta as any)?.env?.VITE_LINE_CHANNEL_ID || (process.env as any)?.REACT_APP_LINE_CHANNEL_ID;
  const REDIRECT_URL =
    (import.meta as any)?.env?.VITE_LINE_REDIRECT_URL ||
    (process.env as any)?.REACT_APP_LINE_REDIRECT_URL ||
    `${window.location.origin}/auth/line`;

  const oauthUrl = useMemo(() => {
    const url = new URL("https://access.line.me/oauth2/v2.1/authorize");
    url.searchParams.set("response_type", "code");
    url.searchParams.set("client_id", CHANNEL_ID ?? "");
    url.searchParams.set("redirect_uri", REDIRECT_URL);
    url.searchParams.set("state", Math.random().toString(36).slice(2));
    url.searchParams.set("scope", "profile openid");
    url.searchParams.set("prompt", "consent");
    return url.toString();
  }, [CHANNEL_ID, REDIRECT_URL]);

  const emitSuccess = useCallback(
    (idToken: string | null, profile: any) => {
      const payload = {
        token: idToken ?? null,
        user: profile
          ? { id: profile.userId, name: profile.displayName, picture: profile.pictureUrl }
          : null,
      };
      if (onSuccess) {
        onSuccess(payload);
      } else {
        try {
          (setAuth as any)(payload.token, payload.user); // 既存 setAuth(token, user) にも対応
        } catch {}
        window.location.href = "/mypage";
      }
    },
    [onSuccess]
  );

  const onClick = useCallback(async () => {
    setError(null);
    try {
      const liff = await ensureLiff();
      if (liff && LIFF_ID) {
        if (!liff.isInit) await liff.init({ liffId: LIFF_ID });
        if (!liff.isLoggedIn()) {
          await liff.login({ redirectUri: REDIRECT_URL });
          return;
        }
        const idToken = liff.getIDToken();
        const profile = await liff.getProfile();
        emitSuccess(idToken, profile);
        return;
      }
    } catch (e: any) {
      console.error("LIFF login error:", e);
      setError(e?.message || "LIFFログインに失敗しました");
    }
    if (!CHANNEL_ID) {
      setError("LINEチャネルIDが未設定です（VITE_LINE_CHANNEL_ID / REACT_APP_LINE_CHANNEL_ID）");
      return;
    }
    window.location.href = oauthUrl;
  }, [LIFF_ID, REDIRECT_URL, CHANNEL_ID, oauthUrl, emitSuccess]);

  return (
    <div style={{ textAlign: "center" }}>
      <button
        onClick={onClick}
        className={className}
        aria-label="LINEでログイン"
        style={{
          ...sizes[size],
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          background: "#06C755",
          color: "#fff",
          border: "none",
          fontWeight: 700,
          lineHeight: 1,
          cursor: "pointer",
          boxShadow: "0 6px 16px rgba(0,0,0,.2)",
        }}
      >
        <span style={{ width: 22, height: 22, display: "inline-block", background: "#fff", borderRadius: 4 }} />
        <span>LINEでログイン</span>
      </button>
      {error && <div style={{ marginTop: 8, fontSize: 12, color: "#f33" }}>{error}</div>}
    </div>
  );
}
